import React, { useState } from 'react';
import Image from 'next/image';
import Slider from 'react-slick';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';

import { Whatshot } from '@styled-icons/material/Whatshot';
import { Raffle, style, useRafflesOrContests } from '../../api';

import {
  TextBold14,
  TextBold36,
  TextRegular12,
} from '../../VuexyDesign/atoms/Text';
import { Button } from '../../VuexyDesign/atoms/_index';
import { formatDateUntil } from '../../utils/formatDateUntil';
import { desktop, noDesktop, useIsDesktop } from '../../style/constants';

const Raffles = () => {
  const [raffles] = useRafflesOrContests({
    highlight: true,
  });

  const isDesktop = useIsDesktop();

  return (
    <Wrapper>
      <Slider
        infinite
        centerPadding={isDesktop ? '30px' : '1px'}
        arrows={false}
        className="center"
        centerMode
        variableWidth
        adaptiveHeight
        swipeToSlide
        pauseOnHover
        autoplay
        autoplaySpeed={5000}
        rows={1}
        cssEase="linear"
        slidesToShow={1}
        slidesToScroll={1}
      >
        {raffles.map((raffle) => (
          <RaffleCard raffle={raffle} key={raffle.id} />
        ))}
      </Slider>
    </Wrapper>
  );
};

export default Raffles;

const Wrapper = styled.div`
  width: 100%;
  display: block;
  min-height: 0px;
  min-width: 0px;
  border-radius: 10px;
`;

const getRaffleImage = (raffle: Raffle) => raffle.media[0]?.url || raffle.image;

const RaffleCard = ({ raffle }: { raffle: Raffle }) => {
  const router = useRouter();
  const [mouseDownPos, setMouseDownPos] = useState<number | undefined>(
    undefined,
  );

  const redirectToPage = () => {
    if (raffle.remix_contest || raffle.contest) {
      router.push(`/contests/${raffle.id}`);
    } else {
      router.push(`/raffles/${raffle.id}`);
    }
  };

  const imageSource = getRaffleImage(raffle);

  return (
    <div
      onMouseUp={(e) => {
        if (mouseDownPos === e.screenX) {
          setMouseDownPos(undefined);
          redirectToPage();
        }
      }}
      onMouseDown={(e) => setMouseDownPos(e.screenX)}
      style={{
        cursor: 'pointer',
      }}
    >
      <RaffleCardWrapper>
        <div
          style={{
            background: `url('${imageSource}')`,
          }}
          className="heroBanner"
        >
          <Ovarlay />
        </div>
        <ContestantsCounter contestants={raffle.total_contestants} />
        <RaffleInfo raffle={raffle} />
        <TimeLeft raffle={raffle} />
      </RaffleCardWrapper>
    </div>
  );
};

const ContestantsCounter = ({ contestants }: { contestants: number }) => {
  const { t } = useTranslation('contests');
  return (
    <ContestantCounterWrapper>
      <Whatshot size={16} color={style.primary_pink_new} />
      <span className="custom-font-text-tiny-normal text-white">
        {contestants} {t('contests:peopleEntered') as string}
      </span>
    </ContestantCounterWrapper>
  );
};

const RaffleInfo = ({ raffle }: { raffle: Raffle }) => (
  <RaffleInfoWrapper>
    <LogoWrapper>
      <Image
        src={raffle.user.profile_photo}
        alt="Label profile"
        fill
        className="pointer-events-none rounded-circle object-fit-cover object-position-center"
        unoptimized
        style={{
          maxWidth: '100%',
        }}
      />
    </LogoWrapper>
    <RaffleMessageWrapper>
      <TextBold36>{raffle.title}</TextBold36>
    </RaffleMessageWrapper>
  </RaffleInfoWrapper>
);

const TimeLeft = ({ raffle }: { raffle: Raffle }) => {
  const { closed_at, open_demo_submission } = raffle;
  const { t } = useTranslation('contests');
  const formattedDate = formatDateUntil(
    closed_at,
    t('common:days') as string,
    t('common:seconds') as string,
  );
  if (open_demo_submission) {
    return (
      <>
        <TimeLeftWrapper>
          <Button
            color="light"
            outline
            className="px-0 w-100 br-5"
            style={{ paddingLeft: '10px', paddingRight: '10px' }}
          >
            {t('contests:joinContest') as string}
          </Button>
        </TimeLeftWrapper>
      </>
    );
  }

  return (
    <>
      {dayjs.unix(closed_at).isBefore(dayjs()) ? (
        <TimeEndedWrapper>
          <Button
            color="secondary"
            outline
            disabled
            className="px-0 w-100 br-5"
            style={{ paddingLeft: '10px', paddingRight: '10px' }}
          >
            {t('contests:contestEnded') as string}
          </Button>
        </TimeEndedWrapper>
      ) : (
        <>
          <TimeLeftWrapper>
            <Button
              color="light"
              outline
              className="px-0 w-100 br-5"
              style={{ paddingLeft: '10px', paddingRight: '10px' }}
            >
              {t('contests:joinContest') as string}
            </Button>
          </TimeLeftWrapper>
          <TextWrapper>
            <span className="custom-font-text-tiny-normal text-white">
              {formattedDate} left
            </span>
          </TextWrapper>
        </>
      )}
    </>
  );
};

const TimeLeftWrapper = styled.div`
  z-index: 5;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 60px;
  width: 80px;

  ${TextBold14}, ${TextRegular12} {
    color: ${style.secondary_white_new};
  }

  ${TextRegular12} {
    display: flex;
    margin-bottom: 2px;
    align-items: center;
    svg {
      margin-right: 4px;
    }
  }

  @media ${desktop} {
    padding: '11px 6px';
    width: 132px;
    height: 55px;
    font-weight: 500;
    bottom: 36px;
    left: 28px;
  }

  @media ${noDesktop} {
    padding: '11px 6px';
    font-weight: 500;
    bottom: 67px;
    width: 90%;
    height: 38px;
    left: 18px;
  }
`;

const TimeEndedWrapper = styled.div`
  z-index: 5;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 60px;
  width: 80px;

  ${TextBold14}, ${TextRegular12} {
    color: ${style.secondary_white_new};
  }

  ${TextRegular12} {
    display: flex;
    margin-bottom: 2px;
    align-items: center;
    svg {
      margin-right: 4px;
    }
  }

  @media ${desktop} {
    padding: '11px 6px';
    width: 132px;
    height: 38px;
    font-weight: 500;
    bottom: 36px;
    left: 28px;
  }

  @media ${noDesktop} {
    padding: '11px 6px';
    font-weight: 500;
    bottom: 67px;
    width: 90%;
    height: 38px;
    left: 18px;
  }
`;

const Ovarlay = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 10px;
  background: rgb(0, 0, 0);
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0.3393732492997199) 50%,
    rgba(0, 0, 0, 0.0760679271708683) 100%
  );
`;

const RaffleInfoWrapper = styled.div`
  z-index: 5;
  position: absolute;
  display: flex;
  flex-direction: column;
  @media ${desktop} {
    left: 27px;
    bottom: 80px;
  }

  @media ${noDesktop} {
    left: 14px;
    bottom: 100px;
    align-items: center;
  }
`;

const RaffleMessageWrapper = styled.div`
  height: 100%;
  @media ${desktop} {
    width: 420px;
  }

  @media ${noDesktop} {
    width: 100%;
    height: 100px;
    margin-left: 4px;
  }
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 14px;
  padding-left: 0px;

  ${TextBold36} {
    color: ${style.secondary_white_new};
    display: -webkit-box;
    text-align: left;
    -webkit-line-clamp: 2;
    @media ${noDesktop} {
      -webkit-line-clamp: 4;
      font-size: 22px;
    }
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;
const TextWrapper = styled.div`
  z-index: 5;
  display: flex;
  align-items: center;
  position: absolute;
  color: ${style.secondary_white_new};
  padding: 0px 8px;

  @media ${desktop} {
    width: 130px;
    left: 180px;
    bottom: 40px;
    height: 30px;
  }

  @media ${noDesktop} {
    left: 20px;
    bottom: 25px;
  }
`;

const ContestantCounterWrapper = styled.div`
  z-index: 5;
  display: flex;
  align-items: center;
  position: absolute;
  left: 283px;
  bottom: 40px;
  height: 30px;
  padding: 0px 8px;
  color: ${style.secondary_white_new};

  @media ${desktop} {
    left: 283px;
    bottom: 40px;
    height: 30px;
    padding: 0px 8px;
  }

  @media ${noDesktop} {
    left: 115px;
    bottom: 18px;
    width: 90%;
  }

  svg {
    margin-right: 8px;
    animation-name: bounce-fire;
    animation-timing-function: ease;
    animation-duration: 0.4s;
    animation-iteration-count: infinite;
  }

  @keyframes bounce-fire {
    0% {
      transform: scale(1, 1) translateY(0);
    }
    10% {
      transform: scale(1.1, 0.9) translateY(0);
    }
    30% {
      transform: scale(0.9, 1.1) translateY(-2px);
    }
    50% {
      transform: scale(1.05, 0.95) translateY(0);
    }
    57% {
      transform: scale(1, 1) translateY(1px);
    }
    64% {
      transform: scale(1, 1) translateY(0);
    }
    100% {
      transform: scale(1, 1) translateY(0);
    }
  }
`;

const LogoWrapper = styled.div`
  height: 100%;
  max-width: 64px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${desktop} {
    width: 54px;
    height: 54px;
    overflow: hidden;
    border-radius: 50%;
  }

  @media ${noDesktop} {
    width: 54px;
    height: 54px;
    overflow: hidden;
    border-radius: 50%;
    align-items: center;
    justify-content: left;
    display: none;
  }
`;

const RaffleCardWrapper = styled.div`
  display: flex;
  position: relative;
  border-radius: 10px !important;
  .heroBanner {
    position: relative;
    width: 100%;
    background-size: cover !important;
    background-repeat: no-repeat !important;
    background-position: center center !important;
    min-height: 360px;
    display: flex;
    align-items: center;
  }
  @media ${desktop} {
    margin: 0px 20px;
    border-radius: 10px;
    width: 1300px;
    height: 435px;
  }

  @media ${noDesktop} {
    margin: 0px 20px;
    border-radius: 10px;
    width: 90vw;
    height: 400px;
  }
`;
